<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <h4 class="mb-1">
        {{ $t('ledger_acc_title') }}
      </h4>
      <b-row>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group :label="$t('ledger_payroll_year')">
            <b-form-select
              v-model.number="year"
              :options="yearOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group :label="$t('ledger_payroll_month')">
            <b-form-select
              v-model.number="month"
              :options="monthOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="canCompany"
          cols="12"
          md="3"
          class="d-flex align-items-end mb-1"
        >
          <b-form-checkbox
            v-model="company"
            @change="load"
          >
            {{ $t('ledger_rep_company_toggle') }}
          </b-form-checkbox>
        </b-col>
      </b-row>
    </b-card>

    <b-card
      v-if="canPost"
      class="rounded-lg"
    >
      <h5>{{ $t('ledger_acc_suggestions') }}</h5>
      <div
        v-if="loadingSuggestions"
        class="text-center py-2"
      >
        <b-spinner />
      </div>
      <div
        v-else-if="!suggestions.length"
        class="text-muted"
      >
        {{ $t('ledger_acc_empty') }}
      </div>
      <div v-else>
        <article
          v-for="row in suggestions"
          :key="row.project_id"
          class="ledger-acc-row"
        >
          <div class="d-flex justify-content-between flex-wrap">
            <div>
              <strong>{{ row.project_name }}</strong>
              <div class="text-muted small">
                {{ row.customer_name }}
              </div>
              <div class="small ledger-num">
                {{ $t('ledger_acc_suggested') }} {{ money(row.suggested_amount) }}
              </div>
            </div>
            <div class="d-flex align-items-end">
              <b-form-input
                v-model.number="row.amount"
                type="number"
                min="0"
                step="0.01"
                class="ledger-acc-amount mr-50"
                :disabled="row.already_accrued"
              />
              <b-button
                size="sm"
                variant="primary"
                class="rounded-pill"
                :disabled="row.already_accrued || postingId === row.project_id || !(Number(row.amount) > 0)"
                @click="confirmPost(row)"
              >
                {{ row.already_accrued ? $t('ledger_acc_already') : $t('ledger_acc_post') }}
              </b-button>
            </div>
          </div>
        </article>
      </div>
    </b-card>

    <b-card class="rounded-lg">
      <h5>{{ $t('ledger_depr_runs') }}</h5>
      <div
        v-if="loading"
        class="text-center py-2"
      >
        <b-spinner />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-muted"
      >
        {{ $t('ledger_acc_empty') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="rows"
            :fields="fields"
            small
            responsive
            class="ledger-dense-table"
          >
            <template #cell(project)="data">
              {{ projectLabel(data.item) }}
            </template>
            <template #cell(amount)="data">
              <span class="ledger-num">{{ money(data.item.amount) }}</span>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="enumValue(data.item.status) === 'posted' ? 'success' : 'dark'">
                {{ enumLabel(data.item.status, $i18n.locale) }}
              </b-badge>
            </template>
            <template #cell(journals)="data">
              <b-link
                v-if="data.item.journal_entry_id && canJournal"
                class="mr-50"
                :to="{ name: 'ledger-journals-view', params: { id: data.item.journal_entry_id } }"
              >
                {{ $t('ledger_journal_open') }}
              </b-link>
              <b-link
                v-if="data.item.reversal_journal_entry_id && canJournal"
                :to="{ name: 'ledger-journals-view', params: { id: data.item.reversal_journal_entry_id } }"
              >
                {{ $t('ledger_depr_reversal_journal') }}
              </b-link>
            </template>
            <template #cell(actions)="data">
              <b-button
                v-if="canPost && enumValue(data.item.status) === 'posted'"
                size="sm"
                variant="outline-danger"
                @click="confirmCancel(data.item)"
              >
                {{ $t('ledger_inv_cancel') }}
              </b-button>
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-acc-row"
          >
            <div>{{ projectLabel(item) }}</div>
            <div class="ledger-num">
              {{ money(item.amount) }}
            </div>
          </article>
        </div>
      </div>
    </b-card>
    <div
      v-if="rows.length"
      class="ledger-acc-sticky d-md-none"
    >
      <span>{{ $t('ledger_assets_total') }}</span>
      <strong class="ledger-num">{{ money(totalPosted) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormSelect, BFormCheckbox, BFormInput, BButton, BTable, BBadge, BLink, BSpinner,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  getRevenueAccruals, getRevenueAccrualSuggestions, createRevenueAccrual, cancelRevenueAccrual,
} from '@/api/accounting/revenueAccruals'
import {
  listFrom, enumValue, enumLabel, formatLedgerMoney, roundMoney, apiErrorMessage,
} from '@/utils/ledger'

function previousMonth() {
  const date = new Date()
  date.setDate(1)
  date.setMonth(date.getMonth() - 1)
  return { year: date.getFullYear(), month: date.getMonth() + 1 }
}

export default {
  name: 'RevenueAccruals',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormSelect, BFormCheckbox, BFormInput, BButton, BTable, BBadge, BLink, BSpinner,
  },
  data() {
    const previous = previousMonth()
    return {
      year: previous.year,
      month: previous.month,
      company: false,
      loading: false,
      loadingSuggestions: false,
      postingId: null,
      suggestions: [],
      rows: [],
    }
  },
  computed: {
    canPost() {
      return this.hasPermission('post revenue_accruals')
    },
    canCompany() {
      return this.hasPermission('view company_reports')
    },
    canJournal() {
      return this.hasPermission('view journal_entries')
    },
    now() {
      const date = new Date()
      return { year: date.getFullYear(), month: date.getMonth() + 1 }
    },
    yearOptions() {
      const years = []
      for (let year = this.now.year; year >= this.now.year - 6; year -= 1) years.push(year)
      return years
    },
    monthOptions() {
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      return Array.from({ length: 12 }, (_, index) => {
        const month = index + 1
        const future = this.year > this.now.year || (this.year === this.now.year && month > this.now.month)
        return {
          value: month,
          text: new Intl.DateTimeFormat(loc, { month: 'long' }).format(new Date(2020, index, 1)),
          disabled: future,
        }
      })
    },
    fields() {
      const fields = [
        { key: 'project', label: this.$t('ledger_payroll_project') },
        { key: 'amount', label: this.$t('ledger_acc_amount') },
        { key: 'status', label: this.$t('ledger_status') },
        { key: 'journals', label: this.$t('ledger_acc_both') },
      ]
      if (this.canPost) fields.push({ key: 'actions', label: this.$t('ledger_actions') })
      return fields
    },
    scopeParams() {
      return this.company && this.canCompany ? { scope: 'company' } : {}
    },
    totalPosted() {
      return roundMoney(this.rows
        .filter(row => enumValue(row.status) === 'posted')
        .reduce((sum, row) => sum + (Number(row.amount) || 0), 0))
    },
  },
  watch: {
    year() { this.load() },
    month() { this.load() },
  },
  mounted() {
    this.load()
  },
  methods: {
    enumValue,
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    projectLabel(row) {
      const project = row.project
      if (project) return project.project_number ? `${project.project_number} — ${project.name || ''}` : (project.name || '—')
      return row.project_name || '—'
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon', variant },
      })
    },
    async load() {
      this.loading = true
      try {
        const res = await getRevenueAccruals({ year: this.year, month: this.month, per_page: 100, ...this.scopeParams })
        this.rows = listFrom(res)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
      if (!this.canPost) return
      this.loadingSuggestions = true
      try {
        const res = await getRevenueAccrualSuggestions({ year: this.year, month: this.month })
        this.suggestions = listFrom(res).map(row => ({
          ...row,
          amount: row.suggested_amount,
        }))
      } catch (error) {
        this.suggestions = []
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.loadingSuggestions = false
      }
    },
    async confirmPost(row) {
      const result = await Swal.fire({
        title: this.$t('ledger_acc_post'),
        text: this.$t('ledger_acc_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_acc_post'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.postingId = row.project_id
      try {
        await createRevenueAccrual({
          project_id: row.project_id,
          year: this.year,
          month: this.month,
          amount: roundMoney(row.amount),
        })
        this.toast(this.$t('ledger_acc_posted'), 'success')
        await this.load()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.postingId = null
      }
    },
    async confirmCancel(row) {
      const result = await Swal.fire({
        text: this.$t('ledger_acc_cancel_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_inv_cancel'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await cancelRevenueAccrual(row.id)
        this.toast(this.$t('ledger_acc_cancelled'), 'success')
        await this.load()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-acc-row { border-bottom: 1px solid #eee; padding: 0.75rem 0; }
.ledger-acc-amount { max-width: 9rem; }
.ledger-acc-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; display: flex;
  justify-content: space-between; background: #fff; border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
